<template>
  <div class="FirstVisit">
    <div class="FirstVisit__content">
      <h2 class="FirstVisit__title">
        {{ $t('welcome_to_look_look_let_s_start_with_some_basic_details') }}
      </h2>
      <LoadingIcon v-if="userFetching" class="FirstVisit__loading"></LoadingIcon>
      <div v-if="!userFetching" class="FirstVisit__form-wrapper">
        <ValidationObserver ref="validation-observer">
          <UserEditModalProfile
            class="FirstVisit__form"
            sms-consent-required
            ai-warning-required
            hide-profile-details
            :user.sync="localUser"
            :phone-verification="phoneVerification"
            :phone-error="phoneError"
            @phone-verified="phoneVerified = $event"
          ></UserEditModalProfile>
        </ValidationObserver>
        <div class="FirstVisit__actions">
          <LLButton
            :action="onSave"
            :loading="savePending"
            :disabled="!valid || !phoneStepDone"
            data-e2e="submitBtn"
          >
            {{ $t('submit') }}
          </LLButton>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ProspectModel } from '@/models/prospect'
import Api from '@/services/api'
import { CompleteRegistrationDestinationType } from '@/common/enums'
import UserEditModalProfile from '@/components/user/UserEditModal/UserEditModalContent/UserEditModalProfile'
import LLButton from '@/components/common/ui-components/LLButton'
import LoadingIcon from '@/components/common/LoadingIcon'
import { localeChanged } from 'vee-validate'
import { phoneErrorMessage } from '@/utils/phoneErrors'
export default {
  name: 'FirstVisit',
  components: { LoadingIcon, LLButton, UserEditModalProfile },
  data() {
    return {
      localUser: null,
      valid: false,
      savePending: false,
      userFetching: false,
      // Set when the study being entered requires a verified phone (#1709)
      phoneVerification: null,
      phoneVerified: false,
      // Message of a failed save that is about the phone, shown under the phone field
      phoneError: ''
    }
  },
  computed: {
    userInStore() {
      return this.$store.state.user.user
    },
    user() {
      return ProspectModel.parseFromApi({ prospect: this.userInStore })
    },
    phoneStepDone() {
      return !this.phoneVerification || this.phoneVerified
    }
  },
  watch: {
    // The error was about the number that was there before the edit
    'localUser.phone'() {
      this.phoneError = ''
    },
    localUser: {
      async handler() {
        this.valid = await this.validate()
        const locale = this.localUser?.language?.toLowerCase()
        if (locale && this.$i18n.locale !== locale) {
          this.$i18n.locale = locale
          this.$store.commit('ui/SET_LOCALE', locale)
          localeChanged()
        }
      },
      deep: true
    }
  },
  async created() {
    this.userFetching = true
    await this.$store.dispatch('user/fetchUser')
    await this.loadPhoneVerification()
    this.localUser = new ProspectModel(this.user)
    this.$nextTick(async () => {
      this.valid = await this.validate()
    })
    this.userFetching = false
  },
  async mounted() {
    this.$nextTick(async () => {
      this.valid = await this.validate()
    })
  },
  methods: {
    async loadPhoneVerification() {
      try {
        const status = await Api.user.phoneVerificationStatus()
        this.phoneVerification = status.required ? { studyId: status.studyId } : null
      } catch (e) {
        // The server still refuses the study until the phone is verified, so a failure here only hides the step
        this.phoneVerification = null
      }
    },
    async onSave() {
      this.savePending = true
      // An already registered participant comes here only to verify the phone, so they go back where they were
      const returnToDestination = !this.localUser.requireRegistration && !!this.phoneVerification
      try {
        const {
          firstName,
          lastName,
          phone,
          image,
          gender,
          birthday,
          language,
          region,
          city,
          country,
          placeId,
          occupation,
          aboutMe,
          interestIdList,
          smsConsentAccepted,
          aiWarningAccepted
        } = this.localUser
        const newUserData = {
          firstName,
          lastName,
          phone,
          gender,
          birthday,
          preferredLanguage: language,
          region,
          city,
          country,
          placeId: placeId || undefined,
          occupation: occupation || null,
          aboutMe: aboutMe || null,
          interestIdList,
          requireRegistration: false,
          imageId: image?.id,
          smsConsentAccepted: smsConsentAccepted || false,
          aiWarningAccepted: aiWarningAccepted || false
        }
        const { prospectAccount } = await Api.user.sync({ ...newUserData })
        this.$store.commit('user/UPDATE_USER', { ...prospectAccount })

        this.$emit('close')

        let locale = this.localUser?.language?.toLowerCase()
        if (locale) {
          this.$i18n.locale = locale
          localeChanged()
          this.$store.commit('ui/SET_LOCALE', locale)
        }
        if (returnToDestination) {
          const { destination, studyId } = this.$store.state.completeRegistration
          await this.$router.push(
            destination === CompleteRegistrationDestinationType.STUDY && studyId ? `/study/${studyId}` : '/'
          )
        } else {
          await this.$router.push('/set-avatar')
        }
      } catch (e) {
        this.phoneError = phoneErrorMessage(e, this.$t.bind(this))
        if (!this.phoneError) this.$errorAlert(e)
      }
      this.savePending = false
    },
    async validate() {
      const validationObserver = this.$refs['validation-observer']
      if (validationObserver) {
        const validationInfo = await validationObserver.validateWithInfo()
        return validationInfo.flags.valid
      }
      return false
    }
  }
}
</script>

<style lang="scss" scoped>
.FirstVisit {
  @apply flex h-full;
  &__title {
    @apply text-center;
  }
  &__loading {
    @apply mx-auto mt-10;
  }
  &__content {
    @apply m-auto py-10 px-10;
    @screen md-max {
      @apply px-4 pt-4;
    }
  }
  &__form-wrapper {
    max-width: 700px;
    @apply w-full m-auto mt-12;
  }
  &__actions {
    @apply mt-8;
  }
}
</style>
